import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import '../../styles/dashboard.css';
import styles from './Dashboard.module.css';
import { useDashboardOverview } from '../../api/dashboard/useDashboardApi';
import { useActionRequired } from '../../api/sytem&settings/useMonitoring';
import { useToast } from '../../context/ToastContext';
import { defaultPeriod, describe, isYmd } from '../../utils/period';

import CommandBar from '../../components/Dashboard/CommandBar/CommandBar';
import ActionRequired from '../../components/Dashboard/ActionRequired/ActionRequired';
import PeriodFilter from '../../components/Dashboard/PeriodFilter/PeriodFilter';
import KpiStrip from '../../components/Dashboard/KpiStrip/KpiStrip';
import MonthlySalesKPI from '../../components/Dashboard/MonthlyKpi/MonthlyKpi';
import BookingFlow from '../../components/Dashboard/BookingFlow/BookingFlow';
import CashFlow from '../../components/Dashboard/CashFlow/CashFlow';
import FinanceHealth from '../../components/Dashboard/FinancialHealth/FinanceHealth';
import RefundOverview from '../../components/Dashboard/RefundOverview/RefundOverview';
import NeedsAttention from '../../components/Dashboard/NeedAttention/NeedAttention';
import LatestActivities from '../../components/Dashboard/LatestActivities/LatestActivities';
import PeopleSnapshot from '../../components/Dashboard/PeopleSnapshot/PeopleSnapshot';
import UsersSnapshot from '../../components/Dashboard/UsersSnapshot/UsersSnapshot';

export default function Dashboard() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { showToast } = useToast();
    const toastedRef = useRef(false);
    const [updatedAt, setUpdatedAt] = useState(null);

    // The analytics period lives in the URL, so a filtered dashboard can be
    // bookmarked and shared. Anything unparseable falls back to this month —
    // the same thing the backend does with a bad query.
    const period = useMemo(() => {
        const from = searchParams.get('from');
        const to = searchParams.get('to');
        if (isYmd(from) && isYmd(to)) return from <= to ? { from, to } : { from: to, to: from };
        return defaultPeriod();
    }, [searchParams]);

    const { data, loading, error, refetch } = useDashboardOverview(period);

    // "Action required" (UI-28): what someone must act on now, polled every minute so a
    // dashboard left open cannot keep saying "Everything is running" on an old answer. Not
    // scoped by the period: an unconfirmed paid booking is urgent whatever month is shown.
    const toastActionError = useCallback((message) => showToast(message, 'error'), [showToast]);
    const action = useActionRequired({ pollMs: 60000, onError: toastActionError });
    const refreshAction = action.refresh;

    const retryAction = useCallback(async () => {
        const res = await refreshAction({ manual: true });
        if (res?.ok) showToast('Action required checked again', 'success');
    }, [refreshAction, showToast]);

    // What the backend actually measured. Panels label themselves from this
    // rather than from the request, so a clamped or corrected period is shown
    // honestly.
    const resolved = data?.period;
    const periodLabel = resolved?.label || describe(period);

    const setPeriod = useCallback(
        (next) => {
            setSearchParams({ from: next.from, to: next.to }, { replace: true });
            showToast(`Showing ${describe(next)}`, 'success');
        },
        [setSearchParams, showToast]
    );

    useEffect(() => {
        if (loading) return;
        if (error) {
            showToast(error, 'error');
            toastedRef.current = true;
            return;
        }
        if (!data) return;
        setUpdatedAt(Date.now());
        // Only announce the first successful load; manual refreshes and period
        // changes are acknowledged by their own controls.
        if (!toastedRef.current) {
            showToast('Dashboard updated with live data', 'success');
            toastedRef.current = true;
        }
    }, [loading, error, data, showToast]);

    useEffect(() => {
        const accessToken = localStorage.getItem('accessToken');

        if (!accessToken || accessToken === 'null' || accessToken === 'undefined' || accessToken.trim() === '') {
            navigate('/login', { replace: true });
            return;
        }

        // JWT tokens have three dot-separated parts; anything else is unusable.
        if (accessToken.split('.').length !== 3) {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            navigate('/login', { replace: true });
        }
    }, [navigate]);

    const handleRefresh = async () => {
        // A failed Action required refresh toasts through the hook's onError.
        const [res] = await Promise.all([refetch(), refreshAction({ manual: true })]);
        if (res?.success) showToast('Dashboard refreshed', 'success');
        else if (res?.message) showToast(res.message, 'error');
    };

    return (
        <div className={styles.wrapper}>
            <CommandBar onRefresh={handleRefresh} loading={loading} updatedAt={updatedAt} />

            <ActionRequired data={action.data} error={action.error} onRetry={retryAction} />

            <div className={styles.grid}>
                <div className={`${styles.span12} ${styles.filterBar}`}>
                    <PeriodFilter
                        period={period}
                        onChange={setPeriod}
                        loading={loading}
                        resolved={resolved}
                    />
                </div>

                <div className={styles.span12}>
                    <KpiStrip data={data} loading={loading} periodLabel={periodLabel} />
                </div>

                <div className={styles.sectionTag}><span>Performance</span></div>
                <div className={styles.span8}>
                    <MonthlySalesKPI
                        series={data?.revenueSeries}
                        revenue={data?.revenue}
                        period={resolved}
                        delta={data?.revenue?.deltaPeriod}
                        loading={loading}
                    />
                </div>
                <div className={styles.span4}>
                    <BookingFlow
                        data={data?.bookingFlow}
                        periodLabel={periodLabel}
                        loading={loading}
                    />
                </div>

                <div className={styles.sectionTag}><span>Finance</span></div>
                <div className={styles.span4}>
                    <CashFlow
                        earnings={data?.earnings}
                        spend={data?.spend}
                        period={resolved}
                        loading={loading}
                    />
                </div>
                <div className={styles.span4}>
                    <FinanceHealth finance={data?.finance} loading={loading} />
                </div>
                <div className={styles.span4}>
                    <RefundOverview
                        refunds={data?.refunds}
                        periodLabel={periodLabel}
                        loading={loading}
                    />
                </div>

                <div className={styles.sectionTag}><span>Operations</span></div>
                <div className={styles.span4}>
                    <NeedsAttention data={data?.needsAttention} loading={loading} />
                </div>
                <div className={styles.span4}>
                    <LatestActivities activity={data?.activity} loading={loading} />
                </div>
                <div className={styles.span4}>
                    <PeopleSnapshot people={data?.people} loading={loading} />
                </div>

                <div className={styles.sectionTag}><span>Access</span></div>
                <div className={styles.span12}>
                    <UsersSnapshot />
                </div>
            </div>
        </div>
    );
}
